import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

interface PatternMetrics {
  basic_stats: {
    mean_diff_pct: number;
    std_diff_pct: number;
    orig_mean: number;
    aligned_mean: number;
    orig_std: number;
    aligned_std: number;
  };
  hourly_patterns: {
    hourly_diffs: Record<number, number>;
    avg_diff_pct: number;
    max_diff_pct: number;
    max_diff_hour: number;
  };
  day_type_patterns: {
    weekday_diff_pct: number;
    weekend_diff_pct: number;
    orig_weekday_mean: number;
    aligned_weekday_mean: number;
    orig_weekend_mean: number;
    aligned_weekend_mean: number;
  };
  seasonal_patterns: {
    seasonal_diffs: Record<string, number>;
    avg_diff_pct: number;
    max_diff_pct: number;
    max_diff_season: string;
  };
  peak_patterns: {
    peak_diff_pct: number;
    offpeak_diff_pct: number;
    ratio_diff_pct: number;
    orig_peak_mean: number;
    aligned_peak_mean: number;
    orig_offpeak_mean: number;
    aligned_offpeak_mean: number;
  };
  range_distribution?: {
    avg_diff_pct: number;
    max_diff_pct: number;
    bin_size: number;
  };
  summary: {
    within_target: number;
    total_metrics: number;
    pct_within_target: number;
    quality: 'excellent' | 'good' | 'fair' | 'poor';
    quality_label: string;
    all_metrics: Array<{
      name: string;
      value: number;
      within_target: boolean;
    }>;
  };
}

interface PatternMetricsResponse {
  success: boolean;
  metrics: PatternMetrics;
  data_type: 'consumption' | 'price';
  original_records: number;
  aligned_records: number;
}

export function usePatternMetrics(
  projectId: string | undefined,
  dataType: 'consumption' | 'price',
  isAligned: boolean // Only fetch if data is aligned
) {
  return useQuery<PatternMetricsResponse>({
    queryKey: ['patternMetrics', projectId, dataType],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await axios.post(
        `${API_BASE_URL}/projects/${projectId}/pattern-metrics`,
        { data_type: dataType }
      );

      return response.data;
    },
    enabled: !!projectId && isAligned, // Only fetch if project exists and data is aligned
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
